import Checkbox from '@ingka/checkbox';
import CheckboxGroup from '@ingka/checkbox-group';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import RadioButtonGroup from '@ingka/radio-button-group';
import Select, { OptGroup, Option } from '@ingka/select';
import TextArea from '@ingka/text-area';

import type { FieldState } from '../store';
import type {
    CheckboxFieldConfig,
    CheckboxGroupFieldConfig,
    FieldOption,
    FieldOptionGroup,
    FormFieldConfig,
    FormFieldValue,
    RadioFieldConfig,
    SelectFieldConfig,
    TextAreaFieldConfig,
    TextFieldConfig,
    TranslateFunction,
} from '../types/form-builder.types';

interface FieldRenderContext {
    readonly fieldState: FieldState;
    readonly translate: TranslateFunction;
    readonly prefixProp: { readonly prefix?: string };
    readonly onChange: (value: FormFieldValue) => void;
    readonly onBlur: () => void;
}

interface FormFieldRendererProps {
    readonly fieldConfig: FormFieldConfig;
    readonly fieldState: FieldState;
    readonly translate: TranslateFunction;
    readonly domIdPrefix: string;
    readonly prefix?: string | undefined;
    readonly onChange: (value: FormFieldValue) => void;
    readonly onBlur: () => void;
}

const getFieldDomId = (domIdPrefix: string, fieldId: string): string => `${domIdPrefix}-${fieldId}`;

const getFieldErrorId = (domId: string): string => `${domId}-error`;

const getFieldHelperId = (domId: string): string => `${domId}-helper`;

const renderInputField = (fieldConfig: TextFieldConfig, context: FieldRenderContext, domId: string) => (
    <InputField
        {...context.prefixProp}
        id={domId}
        type={fieldConfig.type}
        label={context.translate(fieldConfig.label)}
        value={
            typeof context.fieldState.value === 'string' || typeof context.fieldState.value === 'number'
                ? String(context.fieldState.value)
                : ''
        }
        onChange={(e) => {
            context.onChange(e.target.value);
        }}
        onBlur={context.onBlur}
        required={context.fieldState.required}
        disabled={context.fieldState.disabled}
        {...(fieldConfig.readOnly ? { readOnly: true } : {})}
    />
);

const renderTextArea = (fieldConfig: TextAreaFieldConfig, context: FieldRenderContext, domId: string) => (
    <TextArea
        {...context.prefixProp}
        id={domId}
        label={context.translate(fieldConfig.label)}
        value={
            typeof context.fieldState.value === 'string' || typeof context.fieldState.value === 'number'
                ? String(context.fieldState.value)
                : ''
        }
        onChange={(e) => {
            context.onChange(e.target.value);
        }}
        onBlur={context.onBlur}
        required={context.fieldState.required}
        disabled={context.fieldState.disabled}
        {...(fieldConfig.readOnly ? { readOnly: true } : {})}
    />
);

const toRadioButtonList = (fieldConfig: RadioFieldConfig, context: FieldRenderContext, domId: string) =>
    fieldConfig.options.map((option: FieldOption) => ({
        id: `${domId}-${option.value}`,
        name: domId,
        value: String(option.value),
        label: context.translate(option.label),
        checked: context.fieldState.value === option.value,
        onChange: () => {
            context.onChange(option.value);
        },
        onBlur: context.onBlur,
        disabled: context.fieldState.disabled,
    }));

const renderRadioGroup = (fieldConfig: RadioFieldConfig, context: FieldRenderContext, domId: string) => (
    <RadioButtonGroup
        {...context.prefixProp}
        name={context.translate(fieldConfig.label)}
        list={toRadioButtonList(fieldConfig, context, domId)}
        required={context.fieldState.required}
    />
);

const renderCheckbox = (fieldConfig: CheckboxFieldConfig, context: FieldRenderContext, domId: string) => {
    const translatedLabel = context.translate(fieldConfig.label);
    const labelProps = fieldConfig.hideLabel ? { 'aria-label': translatedLabel } : { label: translatedLabel };

    return (
        <Checkbox
            {...context.prefixProp}
            id={domId}
            name={domId}
            value={fieldConfig.id}
            checked={context.fieldState.value === true}
            onChange={() => {
                context.onChange(context.fieldState.value !== true);
            }}
            onBlur={context.onBlur}
            disabled={context.fieldState.disabled}
            {...labelProps}
            {...(fieldConfig.subtle ? { subtle: true } : {})}
        />
    );
};

const toCheckboxList = (fieldConfig: CheckboxGroupFieldConfig, context: FieldRenderContext, domId: string) => {
    const selectedValues = Array.isArray(context.fieldState.value) ? context.fieldState.value : [];

    return fieldConfig.options.map((option: FieldOption) => ({
        id: `${domId}-${option.value}`,
        name: domId,
        value: String(option.value),
        label: context.translate(option.label),
        checked: selectedValues.includes(option.value),
        onChange: () => {
            const next = selectedValues.includes(option.value)
                ? selectedValues.filter((value) => value !== option.value)
                : [...selectedValues, option.value];
            context.onChange(next);
        },
        onBlur: context.onBlur,
        disabled: context.fieldState.disabled,
    }));
};

const renderCheckboxGroup = (fieldConfig: CheckboxGroupFieldConfig, context: FieldRenderContext, domId: string) => (
    <CheckboxGroup
        {...context.prefixProp}
        name={context.translate(fieldConfig.label)}
        list={toCheckboxList(fieldConfig, context, domId)}
        required={context.fieldState.required}
    />
);

const getSelectValue = (value: FormFieldValue): string => {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    return '';
};

const isOptionGroup = (option: FieldOption | FieldOptionGroup): option is FieldOptionGroup => 'groupName' in option;

const getAllSelectOptions = (options: ReadonlyArray<FieldOption | FieldOptionGroup>): readonly FieldOption[] =>
    options.flatMap((option) => (isOptionGroup(option) ? option.options : [option]));

const renderSelectOptions = (options: ReadonlyArray<FieldOption | FieldOptionGroup>, translate: TranslateFunction) =>
    options.map((option) => {
        if (isOptionGroup(option)) {
            return (
                <OptGroup key={option.groupName} groupName={translate(option.groupName)}>
                    {option.options.map((groupOption) => (
                        <Option
                            key={groupOption.value}
                            value={String(groupOption.value)}
                            name={translate(groupOption.label)}
                        />
                    ))}
                </OptGroup>
            );
        }
        return <Option key={option.value} value={String(option.value)} name={translate(option.label)} />;
    });

const renderSelect = (fieldConfig: SelectFieldConfig, context: FieldRenderContext, domId: string) => (
    <Select
        {...context.prefixProp}
        id={domId}
        label={context.translate(fieldConfig.label)}
        value={getSelectValue(context.fieldState.value)}
        onChange={(e) => {
            const allOptions = getAllSelectOptions(fieldConfig.options);
            const selected = allOptions.find((option) => String(option.value) === e.target.value);
            if (selected) {
                context.onChange(selected.value);
            }
        }}
        onBlur={context.onBlur}
        required={context.fieldState.required}
        disabled={context.fieldState.disabled}
        {...(fieldConfig.hintText ? { hintText: context.translate(fieldConfig.hintText) } : {})}
    >
        {renderSelectOptions(fieldConfig.options, context.translate)}
    </Select>
);

const renderField = (fieldConfig: FormFieldConfig, context: FieldRenderContext, domId: string) => {
    switch (fieldConfig.type) {
        case 'text':
        case 'number':
        case 'email': {
            return renderInputField(fieldConfig, context, domId);
        }
        case 'textarea': {
            return renderTextArea(fieldConfig, context, domId);
        }
        case 'radio': {
            return renderRadioGroup(fieldConfig, context, domId);
        }
        case 'checkbox': {
            return renderCheckbox(fieldConfig, context, domId);
        }
        case 'checkbox-group': {
            return renderCheckboxGroup(fieldConfig, context, domId);
        }
        case 'select': {
            return renderSelect(fieldConfig, context, domId);
        }
    }
};

const getCharacterLimit = (fieldConfig: FormFieldConfig): number | undefined => {
    if (
        fieldConfig.type === 'text' ||
        fieldConfig.type === 'number' ||
        fieldConfig.type === 'email' ||
        fieldConfig.type === 'textarea'
    ) {
        return fieldConfig.characterLimit;
    }
    return undefined;
};

export const FormFieldRenderer = ({
    fieldConfig,
    fieldState,
    translate,
    domIdPrefix,
    prefix,
    onChange,
    onBlur,
}: FormFieldRendererProps) => {
    const prefixProp = prefix ? { prefix } : {};
    const context: FieldRenderContext = { fieldState, translate, prefixProp, onChange, onBlur };
    const domId = getFieldDomId(domIdPrefix, fieldConfig.id);

    const hasError = fieldState.error !== undefined;
    const shouldValidate = fieldState.isTouched && hasError;
    const characterLimit = getCharacterLimit(fieldConfig);

    const validationProps = hasError
        ? { validation: { msg: translate(fieldState.error), id: getFieldErrorId(domId) } }
        : {};

    const fieldHelperProps = fieldState.helperText
        ? { fieldHelper: { msg: translate(fieldState.helperText), id: getFieldHelperId(domId) } }
        : {};

    const characterLimitProps = characterLimit ? { characterLimit } : {};

    return (
        <FormField
            {...prefixProp}
            shouldValidate={shouldValidate}
            valid={!hasError}
            disabled={fieldState.disabled}
            {...validationProps}
            {...fieldHelperProps}
            {...characterLimitProps}
        >
            {renderField(fieldConfig, context, domId)}
        </FormField>
    );
};
